/**
 * One setting: what it is, what it does, and the switch (L1, F5).
 *
 * The same four toggles appear in the lobby's Game options tab and in the
 * in-game host sheet, and they were written out twice - which is the shape of
 * mistake this session has already found three times over (the accent wash in
 * ten rules, `duration` in two files, the turn timer's whole block). One
 * component, used in both places.
 *
 * The row is the target, not the checkbox. A 22px box is a small thing to hit
 * on a phone, and the label beside it was already clickable by being inside the
 * <label> - this just makes that visible.
 *
 * The description sits under the title rather than running on from it. As one
 * line, "Let people watch — off means players only" reads as a single sentence
 * whose second half contradicts its first; stacked, it reads as a name and a
 * consequence.
 */

import { Icon } from '../render/Icon.js';
import type { IconName } from '../render/Icon.js';

export function SettingRow({
  icon,
  title,
  help,
  checked,
  onChange,
}: {
  /**
   * A drawn mark, not an emoji.
   *
   * These were emoji - a floppy disk standing in for "keep dealing" on some
   * platforms and a recycling symbol on others, because an emoji is whatever
   * the operating system decides it is. The icon set is ours and looks the
   * same everywhere. Decorative either way: the title says what this is, so it
   * is hidden from anything that speaks.
   */
  icon?: IconName | undefined;
  title: string;
  help: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="setting-row">
      {icon ? (
        <span className="setting-icon">
          <Icon name={icon} />
        </span>
      ) : null}
      <span className="setting-text">
        <span className="setting-title">{title}</span>
        <span className="setting-help">{help}</span>
      </span>
      {/*
        A real checkbox, styled. A div with role="switch" would have to
        reimplement focus, the space key, and how a screen reader announces
        state - all of which this already has for free.
      */}
      <input
        type="checkbox"
        className="setting-switch"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      {/* The state in words, because a switch's position is the one thing about
          it that colour alone has to carry otherwise. */}
      <span className="setting-state" aria-hidden="true">
        {checked ? 'On' : 'Off'}
      </span>
    </label>
  );
}

/**
 * The same row, around a control that is not a switch.
 *
 * The turn timer and the house rules are the same shape as the toggles they
 * sit among — mark, name, consequence, control — but their control is a
 * segmented picker or a stepper, and a row can only be a `<label>` if there is
 * exactly one thing for it to label.
 *
 * So this is a `<div>` and `SettingRow` above stays a `<label>`. That is the
 * whole reason there are two: making the row itself the tap target is what a
 * `<label>` buys, and it is worth more than folding six lines of markup
 * together would be. They share every class, so they cannot drift visually.
 */
export function SettingShell({
  icon,
  title,
  help,
  children,
}: {
  icon?: IconName | undefined;
  title: string;
  help?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <div className="setting-row">
      {icon ? (
        <span className="setting-icon">
          <Icon name={icon} />
        </span>
      ) : null}
      <span className="setting-text">
        <span className="setting-title">{title}</span>
        {help ? <span className="setting-help">{help}</span> : null}
      </span>
      {children}
    </div>
  );
}
